import React, {useEffect} from "react";
import axios from "axios";
import FileSaverBase from 'filesaver.js-npm';
import {
    Button,
    FormControlLabel,
    Grid,
    IconButton,
    LinearProgress,
    Paper,
    Radio,
    RadioGroup,
    Typography,
} from "@mui/material";
import {ArrowBack} from "@mui/icons-material";
import Helpers, {StorageHelper} from "../../../helpers";
import EnhancedErrorDialog from "../../../components/EnhancedErrorDialog";
import EnhancedSelect from "../../../components/EnhancedSelect";
import FilterBlock from "../../catalogmanager/containers/export/components/FilterBlock";
import {utilExportInventory, utilExportInventoryByPartnerIds, utilGetPartners, utilGetSupplierGroups} from "../../../utils/utils";
import ContentLoader from "../../../components/content_loader";
import {fileTypes} from "../../catalogmanager/containers/export/advanced_export";
import {pageContentGap, Header, SectionTitle} from "../../../theme";
import type { GroupOption, InventoryErrorState, InventoryProfile, Partner, PartnerId, PartnerMode, RouteComponentPropsLike, SetTitle, SupplierGroup } from "./types";
const FileSaver: any = FileSaverBase;
const iconButtonSx = (theme) => ({
    backgroundColor: "white",
    border: "none",
    borderRadius: "6px",
    "& svg": { color: "rgba(0, 0, 0, 0.54)" }
});
//region Styles and StyledComponents
//endregion
const blankProfile = {
    Name: '',
    Delimiter: ",",
    FileType: "inventoryFeed",
    OutputFormat: "csv",
    Partner: "",
    Filter: undefined,
    Fields: [],
    Rules: []
};
const storedIdKey = "LBInventoryExportPartnerId";
const IN_GROUP_SENTINEL = "__inGroup__";
const IN_GROUP_LABEL = "In Group (select the Supplier Group below)";
interface InventoryManagementExportProps extends RouteComponentPropsLike {
    isRetailer?: boolean;
    isBillable?: boolean;
    setTitle?: SetTitle;
    signalr?: unknown;
}

function InventoryManagementExport(props: InventoryManagementExportProps) {
    const {setTitle} = props;
    const storageHelper = new StorageHelper();
    //region Component State
    const [error, setError] = React.useState<InventoryErrorState>({open: false, errorHeader: "", errorMessage: ""});
    const [loading, setLoading] = React.useState(false);
    const [updating, setUpdating] = React.useState(false);
    const [partnerId, setPartnerId] = React.useState<PartnerId | undefined | "">(undefined);
    const [partnerMode, setPartnerMode] = React.useState<PartnerMode>("single");
    const [partners, setPartners] = React.useState<Partner[]>([]);
    const [profile, setProfile] = React.useState<InventoryProfile>(blankProfile);
    const [groups, setGroups] = React.useState<SupplierGroup[]>([]);
    const [groupsLoading, setGroupsLoading] = React.useState(false);
    const [selectedGroups, setSelectedGroups] = React.useState<GroupOption[]>([]);
    //endregion
    //region Functions
    const exportItems = async () => {
        const fileType = profile.FileType;
        const format = profile.OutputFormat;
        const matched = fileType === "retailerFeed" || fileType === "inventoryFeed";
        const transform = fileType === "retailerFeed";
        const modifiedAfter = profile.ModifiedAfter;
        const filter = profile.Filter;
        setUpdating(true);
        try {
            let response;
            if (partnerMode === "inGroup") {
                // Derive partner ids from every selected group. The raw
                // /api/v3/Partners/SupplierGroups response shapes members as
                // Partners: [{Id, ...}] (see partnermanager consumers, e.g.
                // GroupSupplierPopover.js / supplier_groups_settings.js). There is no
                // top-level PartnerIds on the raw payload; fall back to it only if a
                // caller pre-derived one.
                // Match on the SAME identity the multiselect uses (getOptionValue:
                // id ?? value). Real /api/v3/Partners/SupplierGroups records are keyed
                // by Name and carry no top-level Id, so sg.id / g.Id are both
                // undefined; matching on String(id) alone made every group compare
                // equal ("undefined" === "undefined") and exported every partner.
                // Falling back to Name keeps 2-supplier groups scoped to their 2
                // suppliers while still preferring Id when one is present.
                const matchedGroups = groups.filter(g =>
                    selectedGroups.some(sg =>
                        String(sg.id ?? sg.value) === String(g.Id ?? g.Name)
                    )
                );
                const partnerIds = Array.from(new Set(
                    matchedGroups.flatMap(g =>
                        Array.isArray(g.Partners)
                            ? g.Partners.map(p => p.Id)
                            : (g.PartnerIds || [])
                    )
                ));
                if (partnerIds.length === 0) {
                    setError({...error, open: true, errorHeader: "Error", errorMessage: "Selected group(s) have no suppliers."});
                    return;
                }
                response = await utilExportInventoryByPartnerIds(partnerIds, format, matched, transform, modifiedAfter, filter);
            } else {
                response = await utilExportInventory(partnerId, format, matched, transform, modifiedAfter, filter);
            }
            if (response.errorMessage) {
                setError({...error, open: true, errorHeader: "Error", errorMessage: response.errorMessage});
                return;
            }
            const fileResponse = await axios({
                url: response.href,
                method: 'GET',
                responseType: 'blob',
            });
            const blob = new Blob([fileResponse.data], { type: fileResponse.headers["content-type"] || "application/octet-stream" });
            FileSaver.saveAs(blob, response.fileName);
        } catch (err: any) {
            console.error("Export error:", err);
            let errorMessage = err.message || "An unexpected error occurred during export";
            if (err.response && err.response.data) {
                let data = err.response.data;
                try {
                    data = JSON.parse(await new Helpers().readBlob(data));
                } catch {
                    // do nothing
                }
                errorMessage = new Helpers().getApiErrors(data).join("\n");
            }
            setError({
                ...error,
                open: true,
                errorHeader: "Error",
                errorMessage
            });
        } finally {
            setUpdating(false);
        }
    }
    const loadPartners = async () => {
        setLoading(true);
        let partners = await utilGetPartners();
        if (partners.errorMessage) return;
        let sortedPartners = partners.sort((a, b) => a.CompanyName.localeCompare(b.CompanyName));
        if(props.isRetailer) {
            sortedPartners.unshift({
                Id: "all",
                CompanyName: "All Partners"
            });
        }
        setPartners(sortedPartners);
        // Get stored partner ID or default to first partner
        let storedId = storageHelper.get(storedIdKey);
        if (!storedId || storedId === IN_GROUP_SENTINEL || !sortedPartners.find(p => p.Id.toString() === storedId.toString())) {
            storedId = sortedPartners[0].Id;
            storageHelper.set(storedIdKey, storedId);
        }
        setPartnerId(storedId);
        setProfile({...profile, Partner: sortedPartners.find(p => p.Id === storedId).CompanyName});
        setLoading(false);
    }
    // Supplier groups are a retailer-only concept; only retailers can scope an
    // export to a group, so we only fetch them for retailer accounts.
    const loadGroups = async () => {
        setGroupsLoading(true);
        const result = await utilGetSupplierGroups();
        if (result && result.errorMessage) {
            setError({open: true, errorHeader: "Error loading supplier groups!", errorMessage: result.errorMessage});
            setGroupsLoading(false);
            return;
        }
        const sortedGroups = (Array.isArray(result) ? result : [])
            .sort((a: SupplierGroup, b: SupplierGroup) => (a.Name || "").localeCompare(b.Name || ""));
        setGroups(sortedGroups);
        setGroupsLoading(false);
    }
    //endregion
    useEffect(() => {
        if(setTitle) {
            setTitle('Inventory Exports', true);
        }
        loadPartners();
        if (props.isRetailer) {
            loadGroups();
        }
        // eslint-disable-next-line
    }, []);
    return (
        <Grid>
            <LinearProgress

                style={{
                    width: "calc(100% + 2em)",
                    height: '0.4em',
                    position: "relative",
                    top: "-1em",
                    right: "1em",
                    visibility: updating ? undefined : 'hidden'
                }}
                variant='indeterminate'/>
            <Grid container style={{gap: pageContentGap, padding: "24px 14px"}}>
                <Grid item container size={12} justifyContent={"flex-start"} style={{gap: "16px"}}>
                    <IconButton
                        sx={iconButtonSx}
                        style={{height: "36px", width: "36px"}}
                        onClick={(e) => {
                            e.preventDefault();
                            if (props.location.key === undefined)
                                props.history.push("/advanced-product-management/v2");
                            else
                                props.history.goBack();
                        }}
                        size="large">
                        <ArrowBack style={{height: "18px", width: "18px"}}/>
                    </IconButton>
                    <Header>
                        Exports
                    </Header>
                </Grid>
                <Grid item container size={12} justifyContent={"flex-start"} style={{gap: "16px"}}>
                    <Paper style={{width: "100%", borderRadius: "5px", padding: "24px"}}>
                        <Grid item container>
                            <SectionTitle>
                                Partner
                            </SectionTitle>
                        </Grid>
                        <Grid item container style={{gap: pageContentGap, flexDirection: "column", textAlign: "left"}}>
                            <EnhancedSelect
                                isClearable
                                loading={loading}
                                value={partnerMode === "inGroup"
                                    ? {value: IN_GROUP_SENTINEL, label: IN_GROUP_LABEL}
                                    : {value: profile.Partner, label: profile.Partner}}
                                options={[
                                    ...(props.isRetailer
                                        ? [{value: IN_GROUP_SENTINEL, label: IN_GROUP_LABEL}]
                                        : []),
                                    ...partners.map(partner => ({
                                        value: partner.CompanyName,
                                        label: partner.CompanyName
                                    }))
                                ]}
                                onChange={(e) => {
                                    if (e == null) {
                                        setPartnerMode("single");
                                        setPartnerId("");
                                        setProfile({...profile, Partner: ""});
                                        return;
                                    }
                                    if (e.value === IN_GROUP_SENTINEL) {
                                        setPartnerMode("inGroup");
                                        setPartnerId("");
                                        setProfile({...profile, Partner: ""});
                                        return;
                                    }
                                    const selectedPartner = partners.find(partner => partner.CompanyName === e.value);
                                    if (selectedPartner !== undefined) {
                                        setPartnerMode("single");
                                        setPartnerId(selectedPartner.Id);
                                        setProfile({...profile, Partner: selectedPartner.CompanyName});
                                    }
                                }}
                            />
                            {partnerMode !== "inGroup" && (partnerId === undefined || partnerId === "") && (
                                <Typography>
                                    Please select a partner to export products from.
                                </Typography>
                            )}
                            {partnerMode === "inGroup" && (
                                <>
                                    <div style={{display: 'flex', flexDirection: 'column'}}>
                                    <SectionTitle>Supplier Group</SectionTitle>
                                    <EnhancedSelect
                                        isMulti
                                        isClearable
                                        loading={groupsLoading}
                                        placeholder="Select supplier group(s)..."
                                        options={groups.map(g => ({value: g.Name, label: g.Name, id: g.Id}))}
                                        // Chips render the group Name (value), but react-select uses the
                                        // option identity for selection/dedup/isSelected. Two groups can
                                        // share a Name, so key identity on the unique Id to keep them
                                        // distinct while the chip still shows the Name.
                                        getOptionValue={(o: any) => String(o.id ?? o.value)}
                                        value={selectedGroups}
                                        onChange={(vals: any) => setSelectedGroups(vals || [])}
                                    />
                                    </div>
                                    {selectedGroups.length === 0 && (
                                        <Typography>
                                            Please select at least one supplier group to export.
                                        </Typography>
                                    )}
                                </>
                            )}
                        </Grid>
                    </Paper>
                </Grid>
                <Grid item container size={12} justifyContent={"flex-start"} style={{gap: "16px"}}>
                    <Paper style={{width: "100%", borderRadius: "5px", padding: "24px"}}>
                        <Grid item container>
                            <SectionTitle style={{margin: 0}}>
                                Filters
                            </SectionTitle>
                        </Grid>
                        {loading ? (
                            <ContentLoader preserveAspectRatio='none' style={{height: '6em', width: '100%'}}
                                           primaryColor='#e1e1e1' secondaryColor='#d8d8d8'/>
                        ) : (
                            <FilterBlock isRetailer={props.isRetailer}
                                         type="inventoryManagement" attributes={[]}
                                         profile={profile} setProfile={setProfile}
                                         noOptions={true}
                            />
                        )}
                    </Paper>
                </Grid>
                <Grid item container size={12} justifyContent={"flex-start"} style={{gap: "16px"}}>
                    <Paper style={{width: "100%", borderRadius: "5px", padding: "24px"}}>
                        <Grid item container style={{marginBottom: "16px"}}>
                            <SectionTitle>
                                Feed Type
                            </SectionTitle>
                        </Grid>
                        <Grid item container style={{textAlign: "left"}}>
                            <RadioGroup
                                value={profile.FileType}
                                onChange={(e) => setProfile({...profile, FileType: e.target.value})}
                                style={{display: "flex", flexDirection: "column", gap: "16px"}}
                            >
                                <FormControlLabel
                                    value="inventoryFeed"
                                    control={<Radio/>}
                                    label={
                                        <Grid container style={{flexDirection: "column"}}>
                                            <Typography style={{fontWeight: 500}}>
                                                Inventory Feed
                                            </Typography>
                                            <Typography style={{fontWeight: 400, color: "#7F7D83"}}>
                                                This will pull a file with all present items in Logicbroker that are
                                                being shared with the retailer and will contain all the
                                                Logicbroker-available fields.
                                            </Typography>
                                        </Grid>
                                    }
                                />
                                <FormControlLabel
                                    value="retailerFeed"
                                    control={<Radio/>}
                                    label={
                                        <Grid container style={{flexDirection: "column"}}>
                                            <Typography style={{fontWeight: 500}}>
                                                Retailer Feed
                                            </Typography>
                                            <Typography style={{fontWeight: 400, color: "#7F7D83"}}>
                                                This will pull a file with all present items in Logicbroker that are
                                                being shared with the retailer and will contain only retailer-specific
                                                fields.
                                            </Typography>
                                        </Grid>
                                    }
                                />
                                <FormControlLabel
                                    value="unmatchedSkuFeed"
                                    control={<Radio/>}
                                    label={
                                        <Grid container style={{flexDirection: "column"}}>
                                            <Typography style={{fontWeight: 500}}>
                                                Unmatched SKU Feed
                                            </Typography>
                                            <Typography style={{fontWeight: 400, color: "#7F7D83"}}>
                                                This will pull a file with all items that do not have a Retailer SKU
                                                associated with the Supplier SKU.
                                            </Typography>
                                        </Grid>
                                    }
                                />
                            </RadioGroup>
                        </Grid>
                    </Paper>
                </Grid>
                <Grid item container size={12} justifyContent={"flex-start"} style={{gap: "16px"}}>
                    <Paper style={{width: "100%", borderRadius: "5px", padding: "24px"}}>
                        <Grid item container style={{marginBottom: "16px"}}>
                            <SectionTitle>
                                Export Settings
                            </SectionTitle>
                        </Grid>
                        <Grid item container style={{gap: pageContentGap, textAlign: "left"}}>
                            <Grid container alignItems={"center"} style={{gap: "45px", flex: 1}}>
                                <Typography style={{width: "200px", fontSize: "16px", fontWeight: 400}}>
                                    Export Format
                                </Typography>
                                <EnhancedSelect
                                    options={fileTypes}
                                    value={fileTypes.find(r => r.value === profile.OutputFormat)}
                                    onChange={(e) => setProfile({...profile, OutputFormat: e.value})}
                                    containerStyle={{flex: 1}}
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
                <Grid item container size={12} justifyContent={"flex-end"}>
                    <Button
                        variant='contained'
                        color='primary'
                        onClick={() => {
                            exportItems();
                        }}
                        disabled={updating || (partnerMode === "inGroup" && selectedGroups.length === 0)}
                    >
                        Export
                    </Button>
                </Grid>
            </Grid>
            {/*region Dialogs and Alerts*/}
            <EnhancedErrorDialog open={error.open} errorHeader={error.errorHeader} errorMessage={error.errorMessage}
                                 onClose={() => {
                                     setError({...error, open: false});
                                     if (loading) props.history.goBack();
                                 }}/>
            {/*endregion*/}
        </Grid>
    );
}
export default InventoryManagementExport;
